import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';

import { formatCurrency } from '../charts/d3/format';
import { GlassSurface } from '../ui/glass';
import { GlassButton } from '../ui/GlassButton';
import { useAuth } from '../../contexts/AuthContext';
import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import {
  listTransactions,
  patchTransaction,
  type ApiTransaction,
} from '../../services/transactionService';
import {
  isOwnTransaction,
  resolveTransactionOwnerLabel,
} from '../../utils/transactionOwnership';
import {
  EditTransactionModal,
  type TransactionEditDraft,
  type TransactionEditTarget,
} from './EditTransactionModal';
import type { SpendDayModalTarget } from './spendDayModal';

export type { SpendDayModalTarget };

type Props = {
  target: SpendDayModalTarget | null;
  onClose: () => void;
};

type Row = {
  id: string;
  merchant: string;
  amount: number;
  direction: 'debit' | 'credit';
  categoryName?: string;
  note?: string;
  userId?: string;
  hiddenFromHousehold?: boolean;
  ownerLabel?: string | null;
  canEdit: boolean;
};

function formatDayTitle(iso: string): string {
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Centered web dialog — DOM scroll so the transaction list scrolls reliably. */
export function SpendDayTransactionsModal({ target, onClose }: Props) {
  const { height, width } = useWindowDimensions();
  const { tokens, user } = useAuth();
  const {
    view: householdView,
    household,
    memberCount,
  } = useHouseholdMembership();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<TransactionEditTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const visible = target != null;
  const panelMaxHeight = Math.min(height * 0.82, 640);
  const listMaxHeight = Math.max(180, panelMaxHeight - 168);

  const mapTx = useCallback(
    (tx: ApiTransaction): Row => {
      const canEdit = isOwnTransaction(tx.userId, user?.id);
      return {
        id: tx.id,
        merchant: tx.merchant,
        amount: Math.abs(tx.amount),
        direction: tx.direction,
        categoryName: tx.categoryName,
        note: tx.description || undefined,
        userId: tx.userId,
        hiddenFromHousehold: tx.hiddenFromHousehold === true,
        ownerLabel: resolveTransactionOwnerLabel({
          userId: tx.userId,
          view: householdView,
          members: household?.members ?? [],
          memberCount,
          viewerUserId: user?.id,
        }),
        canEdit,
      };
    },
    [household?.members, householdView, memberCount, user?.id],
  );

  useEffect(() => {
    if (!target || !tokens?.accessToken) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);

    void listTransactions({
      accessToken: tokens.accessToken,
      tab: 'spend',
      date: target.date,
      categorySlug: target.categorySlug,
      view: householdView,
      limit: 100,
    })
      .then((response) => {
        if (cancelled) return;
        setRows(response.transactions.map(mapTx));
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [target, tokens?.accessToken, householdView, mapTx, reloadKey]);

  const handleSaveEdit = (draft: TransactionEditDraft) => {
    if (!editTarget) return;
    setSaving(true);
    void patchTransaction(editTarget.id, {
      categorySlug: draft.categorySlug,
      categoryName: draft.categoryName,
      hiddenFromHousehold: draft.hiddenFromHousehold,
    })
      .then(() => {
        setEditTarget(null);
        setReloadKey((k) => k + 1);
      })
      .catch((err: Error) => {
        setError(err.message);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.dismissArea}
          onPress={onClose}
          accessibilityLabel="Dismiss day transactions"
        />

        <View
          style={[
            styles.panelWrap,
            {
              maxWidth: Math.min(520, width - spacing.lg * 2),
              maxHeight: panelMaxHeight,
            },
          ]}
          pointerEvents="box-none"
        >
          <GlassSurface
            tint="neutral"
            rounded="xl"
            showHairline
            style={[styles.panel, { maxHeight: panelMaxHeight }]}
            contentStyle={styles.panelContent}
          >
            <View style={styles.header}>
              <View style={styles.headerText}>
                <Text style={styles.eyebrow}>
                  {target?.categoryName ? target.categoryName : 'All spending'}
                </Text>
                <Text style={styles.title}>
                  {target ? formatDayTitle(target.date) : ''}
                </Text>
                <Text style={styles.sub}>
                  {target ? formatCurrency(target.amount) : ''} · newest first
                </Text>
              </View>
              <GlassButton variant="ghost" size="sm" onPress={onClose}>
                Close
              </GlassButton>
            </View>

            {error ? <Text style={styles.error}>{error}</Text> : null}

            {loading ? (
              <ActivityIndicator color={colors.accent} style={styles.loader} />
            ) : (
              <div
                style={{
                  maxHeight: listMaxHeight,
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {rows.length === 0 ? (
                  <Text style={styles.empty}>No transactions on this day.</Text>
                ) : (
                  rows.map((tx) => {
                    const signed =
                      tx.direction === 'credit'
                        ? formatCurrency(tx.amount)
                        : `−${formatCurrency(tx.amount)}`;
                    const rowInner = (
                      <>
                        <View style={styles.rowMain}>
                          <Text style={styles.merchant} numberOfLines={1}>
                            {tx.merchant}
                          </Text>
                          {tx.ownerLabel ? (
                            <Text style={styles.owner} numberOfLines={1}>
                              {tx.ownerLabel}
                            </Text>
                          ) : null}
                          {!target?.categorySlug && tx.categoryName ? (
                            <Text style={styles.category} numberOfLines={1}>
                              {tx.categoryName}
                            </Text>
                          ) : null}
                          {tx.note && tx.note !== tx.merchant ? (
                            <Text style={styles.note} numberOfLines={1}>
                              {tx.note}
                            </Text>
                          ) : null}
                        </View>
                        <Text
                          style={[
                            styles.amount,
                            tx.direction === 'credit' && styles.amountCredit,
                          ]}
                        >
                          {signed}
                        </Text>
                      </>
                    );
                    if (tx.canEdit) {
                      return (
                        <Pressable
                          key={tx.id}
                          style={styles.row}
                          onPress={() =>
                            setEditTarget({
                              id: tx.id,
                              merchant: tx.merchant,
                              categoryName: tx.categoryName,
                              hiddenFromHousehold: tx.hiddenFromHousehold,
                            })
                          }
                          accessibilityLabel={`Edit ${tx.merchant}`}
                        >
                          {rowInner}
                        </Pressable>
                      );
                    }
                    return (
                      <View key={tx.id} style={styles.row}>
                        {rowInner}
                      </View>
                    );
                  })
                )}
              </div>
            )}
          </GlassSurface>
        </View>
      </View>

      <EditTransactionModal
        visible={editTarget != null}
        initial={editTarget}
        saving={saving}
        onClose={() => setEditTarget(null)}
        onSave={handleSaveEdit}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  dismissArea: {
    ...StyleSheet.absoluteFillObject,
  },
  panelWrap: {
    width: '100%',
    zIndex: 1,
  },
  panel: {
    width: '100%',
    borderRadius: radii.xl,
  },
  panelContent: {
    padding: spacing.lg,
    gap: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  eyebrow: {
    ...typography.caption,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  title: {
    ...typography.headline,
    color: colors.text,
    fontSize: 20,
  },
  sub: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  loader: {
    marginVertical: spacing.xl,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  rowMain: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  merchant: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  owner: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  category: {
    ...typography.caption,
    color: colors.accent,
  },
  note: {
    ...typography.caption,
    color: colors.textMuted,
  },
  amount: {
    ...typography.body,
    color: colors.warning,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  amountCredit: {
    color: colors.accent,
  },
  empty: {
    ...typography.body,
    color: colors.textMuted,
    paddingVertical: spacing.lg,
  },
  error: {
    ...typography.caption,
    color: colors.warning,
  },
});
